import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { authAPI } from '@/services/authAPI';
import { User, Mail, Lock, Briefcase, ChevronRight, Phone } from 'lucide-react';
import PasswordInput from '@/components/shared/PasswordInput';
import ThemeToggle from '@/components/shared/ThemeToggle';
import WhatsAppPhoneVerify from '@/components/shared/WhatsAppPhoneVerify';

const APPLICANT_COPY = [
  'Did you notice? Someone just took a',
  'Ready for your',
  "Time for a",
  "Stuck for a long time? Here's your",
  "Your dream job is one click away, time for a",
  "Top companies are hunting for YOU, make your",
  "Your skills deserve the spotlight, it's time for a",
  "The job market is on fire, ready for your",
  "Stand out from 10,000 applicants, make your",
  "AI-matched to your dream role, time for a",
  "Let your career take flight with a",
  "Stop scrolling job boards, start your",
  "Your next big break starts with a",
  "The best version of your career starts with a",
  "3 recruiters just went online, time for your",
  "That salary hike won't find itself, make your",
  "Fortune favors the bold, take your",
  "Monday motivation? How about a",
  "Bored at work? Sounds like you need a",
  "Plot twist: you get the offer after your",
  "Your LinkedIn is tired, let's try a",
  "5 new jobs just matched you, it's",
  "One small step for you, one giant",
  "Life's too short for bad jobs, make your",
  "Coffee's ready, resume's ready, time for your",
  "The universe is hinting at your",
  "Spoiler alert: you're getting hired after your",
];

const RECRUITER_COPY = [
  "Let's find candidates who took a",
  "Ready for your",
  "Time for a",
  "That req won't fill itself, make your",
  "Talented folks are waiting for your",
  "Got a new req to fill? Make your",
  "Top candidates are actively looking, time for your",
  "Build your dream team, start with a",
  "AI finds the needle in the haystack, make your",
  "Great talent won't wait forever, time for your",
  "Screening just got 10x faster, it's time for a",
  "Hire smarter not harder, start your",
  "Your next star employee is waiting for your",
  "Close that role this week, make your",
  "Jay screened 200 candidates overnight for your",
  "Zero ghosting, all talent, time for your",
  "Your hiring pipeline needs a",
  "HR said ASAP, so let's do a quick",
  "That unicorn candidate? Jay found them for your",
  "Inbox full of resumes? Let AI sort your",
  "Your team is one hire away from perfect, make your",
  "3 candidates just went live, time for your",
  "Budget approved! Now make your",
  "The perfect JD writes itself with a",
  "Skip the sourcing grind, try a",
  "Plot twist: the candidate accepts after your",
];

function RotatingCopy({ activeRole, alwaysDark = false }) {
  const copyList = activeRole === 'recruiter' ? RECRUITER_COPY : APPLICANT_COPY;
  const [index, setIndex] = useState(0);
  const [animState, setAnimState] = useState('visible'); // visible | scrolling-out | scrolling-in

  useEffect(() => {
    setIndex(Math.floor(Math.random() * copyList.length));
  }, [activeRole, copyList.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimState('scrolling-out');
      setTimeout(() => {
        setIndex(prev => {
          let next = prev + 1;
          if (next >= copyList.length) next = 0;
          return next;
        });
        setAnimState('scrolling-in');
        setTimeout(() => setAnimState('visible'), 80);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, [copyList.length]);

  const transformStyle = {
    visible: 'translateY(0)',
    'scrolling-out': 'translateY(-110%)',
    'scrolling-in': 'translateY(110%)',
  }[animState];

  const opacityStyle = animState === 'visible' ? 1 : 0;
  const transitionStyle = animState === 'scrolling-in' ? 'none' : 'transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease';

  return (
    <div className="h-[28px] overflow-hidden relative mb-2" data-testid="rotating-copy-container">
      <p className={`${alwaysDark ? 'text-zinc-400' : 'text-slate-500 dark:text-zinc-400'} text-base leading-7 whitespace-nowrap`}
        style={{
          fontFamily: "'Outfit', sans-serif",
          transform: transformStyle,
          opacity: opacityStyle,
          transition: transitionStyle,
        }}
        data-testid="rotating-copy-text">
        {copyList[index]}
      </p>
    </div>
  );
}

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('applicant');
  const [loginRole, setLoginRole] = useState('applicant');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [legalVersions, setLegalVersions] = useState({ terms_version: '', privacy_version: '' });
  const [waInfo, setWaInfo] = useState(null);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const { login, register } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const modeParam = searchParams.get('mode');
  const refParam = searchParams.get('ref');

  useEffect(() => {
    if (modeParam === 'signup') setIsRegister(true);
    else if (modeParam === 'signin') setIsRegister(false);
  }, [modeParam]);

  // Pre-fill the referral code from ?ref= (from a referrer's shared link) and force signup mode
  useEffect(() => {
    if (refParam) {
      const clean = String(refParam).replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 12);
      if (clean) {
        setReferralCode(clean);
        setIsRegister(true);
      }
    }
  }, [refParam]);

  useEffect(() => {
    if (roleParam === 'applicant') { setRole('applicant'); setLoginRole('applicant'); }
    else if (roleParam === 'recruiter' || roleParam === 'hiring') { setRole('recruiter'); setLoginRole('recruiter'); }
  }, [roleParam]);

  useEffect(() => {
    authAPI.getLegalVersions().then(setLegalVersions).catch(() => { /* ignore */ });
    authAPI.getWhatsAppInfo().then(setWaInfo).catch(() => { /* ignore */ });
  }, []);

  // Determine which role is currently "active" for copy selection
  const activeRole = isRegister ? role : loginRole;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isRegister && !acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    if (isRegister && !phoneVerificationToken) {
      setError('Please verify your phone number via WhatsApp before creating your account.');
      return;
    }
    setLoading(true);
    try {
      let user;
      if (isRegister) {
        user = await register(email, password, role, phone, {
          terms_version: legalVersions.terms_version,
          privacy_version: legalVersions.privacy_version,
        }, phoneVerificationToken, role === 'applicant' ? referralCode : null);
      } else {
        user = await login(email, password, loginRole);
      }
      const r = user.role || user.active_role;
      if (r === 'applicant') navigate('/applicant');
      else if (r === 'recruiter') navigate('/recruiter');
      else if (r === 'admin') navigate('/admin');
      else if (r === 'support_agent') navigate('/support');
      else if (r === 'staffing_head' || r === 'staffing_sub') navigate('/staffing');
      else navigate('/');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'applicant', label: 'Applicant', icon: User, desc: 'Find opportunities' },
    { value: 'recruiter', label: 'Recruiter', icon: Briefcase, desc: 'Find talent' },
  ];

  // Emergent-managed Google Auth — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    // Remember which role to assign for brand-new Google users
    localStorage.setItem('google_auth_role', isRegister ? role : loginRole);
    const redirectUrl = window.location.origin + '/auth/google/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // WhatsApp signup deep link — backend info first, env fallback so the button always shows
  const waFallbackNumber = process.env.REACT_APP_WHATSAPP_NUMBER;
  const waLink = (waInfo?.enabled && waInfo.wa_link) || (waFallbackNumber ? `https://wa.me/${waFallbackNumber}?text=Hi%20Numuv` : null);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex" data-testid="login-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <Link to="/" data-testid="back-home-link" className="fixed top-5 left-5 z-50 text-sm text-slate-500 dark:text-zinc-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors lg:text-zinc-400 lg:dark:text-zinc-500">
        ← Home
      </Link>
      {/* Left Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-gradient-to-br from-[#0a0a0b] via-[#111114] to-[#0a0a0b]">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, #555 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-emerald-600/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />

        <div className="relative z-10 px-16 max-w-lg">
          <RotatingCopy activeRole={activeRole} alwaysDark />

          <h1 className="text-6xl font-bold tracking-tight mb-6" style={{fontFamily: "'DM Serif Display', serif"}}>
            <span className="text-white">nu</span>
            <span className="text-emerald-400">mu</span>
            <span className="text-white">v</span>
          </h1>

          <p className="text-zinc-500 leading-relaxed mb-10">AI-powered recruitment connecting talent with opportunity. Smart matching, automated screening, and Jay — your personal career coach.</p>

          <div className="space-y-3">
            {['AI Candidate Matching', 'Jay — Career Coach Bot', 'Voice-Based Screening', 'Smart Analytics'].map((f, i) => (
              <div key={f} className="flex items-center gap-3 text-zinc-400 animate-[fadeSlideUp_0.5s_ease-out_forwards]" style={{animationDelay: `${i * 150}ms`, opacity: 0}}>
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-sm">{f}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 flex items-center gap-3">
            <svg width="32" height="26" viewBox="0 0 52 40">
              <ellipse cx="26" cy="24" rx="16" ry="12" fill="#3B82F6"/><circle cx="38" cy="16" r="10" fill="#60A5FA"/>
              <path d="M38 6 L42 2 L40 8 L44 5 L41 10" fill="#1D4ED8"/><circle cx="41" cy="14" r="3" fill="white"/>
              <circle cx="42" cy="14" r="1.5" fill="#1E293B"/><path d="M48 17 L52 16 L48 19Z" fill="#F59E0B"/>
              <ellipse cx="28" cy="28" rx="10" ry="6" fill="#BFDBFE"/>
            </svg>
            <span className="text-zinc-600 text-xs italic">&quot;I&apos;ll help you land that dream role!&quot;</span>
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-20 lg:py-10">
        <div className="w-full max-w-md">
          {/* Mobile header with rotating copy */}
          <div className="lg:hidden mb-8 pt-2">
            <RotatingCopy activeRole={activeRole} />
            <h1 className="text-3xl font-bold" style={{fontFamily: "'DM Serif Display', serif"}}>
              <span className="text-slate-900 dark:text-white">nu</span><span className="text-emerald-600 dark:text-emerald-400">mu</span><span className="text-slate-900 dark:text-white">v</span>
            </h1>
          </div>

          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">{isRegister ? 'Create account' : 'Welcome back'}</h2>
          <p className="text-slate-500 dark:text-zinc-500 mb-8">{isRegister ? 'Get started with Numuv' : 'Sign in to your account'}</p>

          {roleParam === 'staffing' && (
            <Link to="/staffing-signup" data-testid="staffing-role-banner"
              className="flex items-center justify-between gap-3 bg-purple-500/10 border border-purple-500/30 text-purple-700 dark:text-purple-300 px-4 py-3 rounded-lg mb-6 text-sm hover:bg-purple-500/15 transition-colors">
              <span>Running a staffing organization? Onboard your org here</span>
              <ChevronRight className="w-4 h-4 shrink-0" />
            </Link>
          )}

          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
                <input data-testid="email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 focus:ring-1 focus:ring-slate-300 dark:focus:ring-zinc-600 transition-colors" />
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
                  <input data-testid="phone-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+919876543210" required className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 focus:ring-1 focus:ring-slate-300 dark:focus:ring-zinc-600 transition-colors" />
                </div>
                <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">International format with country code. We&apos;ll send you a WhatsApp OTP to verify.</p>
                <WhatsAppPhoneVerify
                  phone={phone}
                  mode="signup"
                  testIdPrefix="signup-wa"
                  disabled={!phone}
                  onVerified={({ phone_verification_token }) => setPhoneVerificationToken(phone_verification_token)}
                  onPhoneUnverified={() => setPhoneVerificationToken('')}
                />
              </div>
            )}

            <div>
              <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">Password</label>
              <PasswordInput
                value={password}
                onChange={e => setPassword(e.target.value)}
                testId="password-input"
                placeholder="Min 6 characters"
                required
                minLength={6}
                autoComplete={isRegister ? 'new-password' : 'current-password'}
              />
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-2">I am a...</label>
                <div className="grid grid-cols-2 gap-3">
                  {roles.map(({ value, label, icon: Icon, desc }) => (
                    <button key={value} type="button" data-testid={`role-${value}`} onClick={() => setRole(value)}
                      className={`p-4 rounded-lg border text-left transition-all ${role === value ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-200 dark:border-zinc-800 bg-white dark:bg-[#111114] hover:border-slate-300 dark:hover:border-zinc-700'}`}>
                      <Icon className={`w-5 h-5 mb-2 ${role === value ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-zinc-500'}`} />
                      <div className={`text-sm font-medium ${role === value ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-zinc-300'}`}>{label}</div>
                      <div className="text-xs text-slate-500 dark:text-zinc-500 mt-0.5">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isRegister && role === 'applicant' && (
              <div data-testid="referral-code-block">
                <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">
                  Referral code <span className="text-slate-400 dark:text-zinc-600">(optional)</span>
                </label>
                <input
                  data-testid="referral-code-input"
                  type="text"
                  value={referralCode}
                  onChange={e => setReferralCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 12))}
                  placeholder="e.g. NUM8HK"
                  className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40 tracking-wider font-mono uppercase"
                />
                <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">
                  Have a code from a friend? You&apos;ll both earn bonus Mu Points when you sign up.
                </p>
              </div>
            )}

            {!isRegister && (
              <div>
                <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-2">Sign in as</label>
                <div className="grid grid-cols-2 gap-3">
                  {roles.map(({ value, label, icon: Icon, desc }) => (
                    <button key={value} type="button" data-testid={`login-role-${value}`} onClick={() => setLoginRole(value)}
                      className={`p-3 rounded-lg border text-left transition-all ${loginRole === value ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-200 dark:border-zinc-800 bg-white dark:bg-[#111114] hover:border-slate-300 dark:hover:border-zinc-700'}`}>
                      <Icon className={`w-4 h-4 mb-1.5 ${loginRole === value ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-zinc-500'}`} />
                      <div className={`text-sm font-medium ${loginRole === value ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-zinc-300'}`}>{label}</div>
                      <div className="text-xs text-slate-500 dark:text-zinc-500 mt-0.5">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isRegister && (
              <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="tos-accept-row">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 accent-emerald-500 h-4 w-4 flex-shrink-0"
                  data-testid="tos-accept-checkbox"
                />
                <span className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
                  I have read and agree to Numuv&apos;s{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="tos-terms-link" className="text-emerald-600 dark:text-emerald-400 hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="tos-privacy-link" className="text-emerald-600 dark:text-emerald-400 hover:underline">Privacy Policy</Link>.
                </span>
              </label>
            )}

            <button type="submit" disabled={loading || (isRegister && (!acceptedTerms || !phoneVerificationToken))} data-testid="submit-btn"
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-black font-medium py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
              {!loading && <ChevronRight className="w-4 h-4" />}
            </button>

            {!isRegister && (
              <div className="flex justify-end -mt-2">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-slate-500 dark:text-zinc-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                  Forgot password?
                </Link>
              </div>
            )}

            {isRegister && role === 'applicant' && waLink && (
              <>
                <div className="relative flex items-center my-2">
                  <div className="flex-1 h-px bg-slate-100 dark:bg-zinc-800" />
                  <span className="px-3 text-xs text-slate-500 dark:text-zinc-600 uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-slate-100 dark:bg-zinc-800" />
                </div>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-signup-btn"
                  className="w-full bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/40 text-emerald-700 dark:text-[#25D366] font-medium py-2.5 rounded-lg transition-all flex items-center justify-center gap-2.5"
                >
                  <svg className="w-4.5 h-4.5" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-sm">Sign up with WhatsApp</span>
                </a>
                <p className="text-[11px] text-slate-500 dark:text-zinc-600 text-center -mt-1" data-testid="whatsapp-signup-hint">
                  Opens WhatsApp — send &quot;Hi Numuv&quot; and Jay will create your account in chat.
                </p>
              </>
            )}

            {/* Google sign-in temporarily hidden — keep handleGoogleSignIn for later re-enable.
            <div className="relative flex items-center my-2">
              <div className="flex-1 h-px bg-slate-100 dark:bg-zinc-800" />
              <span className="px-3 text-xs text-slate-500 dark:text-zinc-600 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-slate-100 dark:bg-zinc-800" />
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              data-testid="google-signin-btn"
              className="w-full bg-white dark:bg-[#111114] hover:bg-slate-100 dark:hover:bg-[#1a1a1f] border border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700 text-slate-900 dark:text-white font-medium py-2.5 rounded-lg transition-all flex items-center justify-center gap-3"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.31 0-6.02-2.74-6.02-6.1S8.69 5.9 12 5.9c1.88 0 3.14.8 3.86 1.48l2.63-2.54C16.95 3.32 14.7 2.4 12 2.4 6.78 2.4 2.55 6.63 2.55 11.9S6.78 21.4 12 21.4c6.92 0 9.45-4.86 9.45-7.34 0-.49-.05-.86-.12-1.24L12 10.2z"/>
              </svg>
              <span className="text-sm">Continue with Google</span>
            </button>
            */}

            <div className="text-center mt-2 space-y-1.5">
              <Link to="/staffing-signup" data-testid="staffing-signup-link" className="text-xs text-slate-500 dark:text-zinc-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors block">
                Run a staffing org? Onboard here →
              </Link>
            </div>
          </form>

          <p className="text-center text-slate-500 dark:text-zinc-500 text-sm mt-6">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button onClick={() => { setIsRegister(!isRegister); setError(''); }} data-testid="toggle-auth" className="text-slate-900 dark:text-white hover:text-slate-600 dark:hover:text-zinc-300 underline underline-offset-4">
              {isRegister ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
